import React, { useMemo, useState } from 'react';
import Field from '../../CreativePreferences/fields/Field';
import '../../CreativePreferences/CreativePreferences.css';
import DatePills from '../components/DatePills';
import OrderCard from '../components/OrderCard';
import CoupleShotCard from '../components/CoupleShotCard';
import TimingCard from '../components/TimingCard';
import InterviewCard from '../components/InterviewCard';
import InterviewBlock from '../components/InterviewBlock';
import ReadyBlock from '../components/ReadyBlock';
import { PickSelect, SearchSelect } from '../components/controls';
import {
  datesOf, eventsOnDate, isAnswered, isPinnedEvent,
} from '../state/derive';
import { fmtDateLong, fmtTime, fmtWeekday } from '../state/format';
import { COUPLE_GROUP, EVENT_SIDES, SIDE, SIDE_LABEL } from '../../constants';
import { useWeddingDay } from '../state/useWeddingDay';

const e = React.createElement;

const sideLabel = (value) => (value === SIDE.GROOM ? 'Groom' : 'Bride');

// Where the catalogue has nothing for what the couple typed, these are the
// general ones to fall back on. They are looked up in the catalogue rather
// than offered on their own: the list is ops's, and an event the estimation
// cannot file is worse than a name that is only close.
const SPARE_RITUALS = ['rituals', 'pooja', 'party'];

// Both sides write this timeline, so a change needs an author beside it.
const ChangeStrip = ({ changeLog }) => {
  if (!changeLog.length) return null;
  return e('div', { className: 'wd-changes' }, [
    e('h4', { key: 'h' }, 'Recent changes'),
    e(
      'ul',
      { key: 'l' },
      changeLog.slice(0, 5).map((row, i) =>
        e(
          'li',
          { key: row.id || i },
          `${sideLabel(row.side)} side: ${row.summary || row.message || row.action || 'updated the timeline'}`
        )
      )
    ),
  ]);
};

// The order the day runs in, then the times it runs at. Two phases on one
// screen, because the couple settles the order first and the clock after.
const OrderPhase = ({ isPhone }) => {
  const {
    events, activeDate, sharedLocked, readOnly, canEdit, fieldErrors, reorder, addRitual,
    ritualOptions, renameEvent, setEventSide, setCoupleShot, setCoupleShotTime,
    removeRitual, setPhase,
  } = useWeddingDay();
  // Read only and submitted close the same controls for different reasons, and
  // saying "submitted" to someone who simply cannot edit is wrong.
  const viewOnly = readOnly || canEdit === false;
  // What closes the controls, whichever reason it is: the server refuses a
  // write from either, so offering one only ends in "We could not save".
  const shut = sharedLocked || viewOnly;
  const [drag, setDrag] = useState({ dragId: null, overId: null });
  const [editingId, setEditingId] = useState(null);
  const [composer, setComposer] = useState({ open: false, name: '', side: SIDE.BRIDE });
  const spareRituals = useMemo(() => SPARE_RITUALS
    .map((want) => (ritualOptions || []).find(
      (o) => String(o.name || '').trim().toLowerCase() === want))
    .filter(Boolean)
    .map((o) => ({ value: o.name, label: o.name })), [ritualOptions]);
  const ritualChoices = useMemo(() => (ritualOptions || [])
    .map((o) => ({ value: o.name, label: o.name })), [ritualOptions]);
  const [announcement, setAnnouncement] = useState('');

  // A day belongs to both of them: the side is a chip on the card, not a view
  // to switch between.
  const day = activeDate ? eventsOnDate(events, activeDate) : [];

  const move = (fromId, toId) => {
    if (!activeDate || String(fromId) === String(toId)) return;
    // Getting ready is held in front of the wedding, so it is not theirs to
    // place. The card says so and offers no grip; this is the second gate,
    // for a drop that reached here another way.
    const moving = day.find((x) => String(x.id) === String(fromId));
    if (moving && isPinnedEvent(moving)) return;
    const ids = day.map((x) => String(x.id));
    const from = ids.indexOf(String(fromId));
    const to = ids.indexOf(String(toId));
    if (from < 0 || to < 0 || from === to) return;
    ids.splice(to, 0, ids.splice(from, 1)[0]);
    reorder(activeDate, ids);
    setAnnouncement('Moved. Timings from this position onward need to be set again.');
  };

  // The keyboard path: one step up or down from the focused card.
  const nudge = (id, dir) => {
    if (shut) return;
    const at = day.findIndex((x) => String(x.id) === String(id));
    const to = at + dir;
    if (at < 0 || to < 0 || to >= day.length) return;
    move(id, day[to].id);
  };

  /**
   * Pointer drag rather than the HTML5 kind, which never fires on touch.
   *
   * A mouse drags as soon as it has travelled a few pixels. A finger has to
   * hold still first, because the same gesture scrolls the page: the card
   * leaves `touch-action: pan-y` so the list scrolls normally, and only once
   * the hold completes does the drag take the gesture over. Moving before
   * then is a scroll, so the hold is abandoned and the browser keeps it.
   */
  const startDrag = (ev, id) => {
    if (shut || ev.button > 0) return;
    // An open dropdown's options are list items, not buttons, so a held
    // option would otherwise pick the card up instead of the side.
    const control = 'button, input, select, textarea, [role="listbox"]';
    if (ev.target.closest(control)) return;

    const touch = ev.pointerType === 'touch';
    const startX = ev.clientX;
    const startY = ev.clientY;
    let dragging = false;
    let over = null;
    let hold = null;

    const begin = () => {
      hold = null;
      dragging = true;
      document.body.classList.add('wd-dragging');
      setDrag({ dragId: String(id), overId: null });
      // Says the card is now the finger's, since nothing has moved yet.
      if (touch && navigator.vibrate) navigator.vibrate(12);
    };

    // The page scrolls on touchmove, and preventDefault stops it only while
    // the scroll has not begun. The finger is still during the hold, so by
    // the time this blocks anything there is nothing under way to fight.
    const onTouchMove = (t) => {
      if (dragging) t.preventDefault();
    };

    const onPointerMove = (m) => {
      const dx = Math.abs(m.clientX - startX);
      const dy = Math.abs(m.clientY - startY);
      if (!dragging) {
        // Travel before the hold completes is a scroll, so the gesture is
        // handed back rather than taken.
        if (touch) {
          if (dx > 8 || dy > 8) finish(false);
          return;
        }
        if (dy < 5) return;
        begin();
      }
      m.preventDefault();
      const hit = document.elementFromPoint(m.clientX, m.clientY);
      const card = hit && hit.closest('[data-wd-entry]');
      const next = card ? card.getAttribute('data-wd-entry') : null;
      if (next !== over) {
        over = next;
        setDrag({ dragId: String(id), overId: next });
      }
    };

    function finish(drop) {
      if (hold) clearTimeout(hold);
      window.removeEventListener('pointermove', onPointerMove);
      window.removeEventListener('pointerup', onPointerUp);
      window.removeEventListener('pointercancel', onPointerUp);
      window.removeEventListener('touchmove', onTouchMove);
      document.body.classList.remove('wd-dragging');
      setDrag({ dragId: null, overId: null });
      if (drop && dragging && over && over !== String(id)) {
        move(String(id), over);
      }
    }

    const onPointerUp = () => finish(true);

    if (touch) hold = setTimeout(begin, 350);
    window.addEventListener('pointermove', onPointerMove, { passive: false });
    window.addEventListener('pointerup', onPointerUp);
    window.addEventListener('pointercancel', onPointerUp);
    window.addEventListener('touchmove', onTouchMove, { passive: false });
  };

  if (!events.length) {
    return e('div', { className: 'wd-empty' }, [
      e('div', { className: 'wd-empty-title', key: 't' }, 'No events yet'),
      e(
        'p',
        { key: 'p' },
        'Your timeline appears here once the events on your booking are set. Contact your coordinator if you expected to see them.'
      ),
    ]);
  }

  return e('div', null, [
    e(DatePills, { key: 'pills' }),
    e(
      'p',
      { className: 'wd-note', key: 'note' },
      viewOnly
        ? 'You are reading this timeline. Only the bride and groom can change the order.'
        : sharedLocked
        ? 'The order is locked now that the form has been submitted. Contact your coordinator to change it.'
        : 'Drag and move the event to the correct position. Either of you can change this, and the other sees it.'
    ),
    e(
      'div',
      { className: 'wd-stack', key: 'stack' },
      day.map((entry, i) => {
        // The couple shot is attached to its event rather than placed in the
        // day: it sits above the card or below it, and moves with it.
        const inside = entry.couple_shot && entry.couple_shot.position === 'during';
        const shot = entry.couple_shot
          ? e(CoupleShotCard, {
              key: `shot-${entry.id}`,
              entry,
              locked: shut,
              onMove: setCoupleShot,
              onPick: setCoupleShotTime,
              inside,
              canMove: true,
            })
          : null;
        const atStart = entry.couple_shot && entry.couple_shot.position === 'start';
        return e('div', { className: 'wd-ogroup', key: String(entry.id) }, [
          // Runs before the couple is needed, so it is drawn above
          // everything this event holds, the shot included. Nothing on it
          // is theirs to set, so it renders itself or renders nothing.
          // Getting ready comes first on the day, so it is drawn above the
          // sitting, which is itself above the shot and the event.
          e(ReadyBlock, { key: 'ready', entry }),
          e(InterviewBlock, { key: 'interview', entry }),
          atStart ? shot : null,
          e(OrderCard, {
            shot: inside ? shot : null,
            key: 'card',
            entry,
            position: i,
            locked: shut,
            isPhone,
            editing: editingId === String(entry.id),
            dragId: drag.dragId,
            overId: drag.overId,
            pinned: isPinnedEvent(entry),
            error: fieldErrors[`side:${entry.id}`] || null,
            onEdit: (id) => setEditingId(id === null ? null : String(id)),
            onRename: renameEvent,
            ritualChoices,
            spareRituals,
            onSide: setEventSide,
            onPointerDown: startDrag,
            onNudge: nudge,
            onRemove: removeRitual,
          }),
          atStart || inside ? null : shot,
        ]);
      })
    ),
    shut
      ? null
      : composer.open
      ? e('div', { className: 'wd-card', key: 'composer', style: { marginTop: 22 } }, [
          e(SearchSelect, {
            key: 'name',
            label: 'Which event is it?',
            value: composer.name,
            placeholder: 'Type to search, for example chooda',
            options: ritualChoices,
            spares: spareRituals,
            spareNote: 'Did not find it? Pick the closest one.',
            onChange: (v) => setComposer((prev) => ({ ...prev, name: v })),
          }),
          e('div', { key: 'side', style: { marginTop: 16 } },
            e(PickSelect, {
              label: 'Whose side is this ritual for?',
              value: composer.side,
              options: EVENT_SIDES,
              onChange: (v) => setComposer((prev) => ({ ...prev, side: v })),
            })),
          e('div', { className: 'wd-acts', key: 'acts', style: { marginTop: 12 } }, [
            e(
              'button',
              {
                key: 'cancel',
                type: 'button',
                className: 'wd-btn wd-btn-ghost',
                onClick: () => setComposer({ open: false, name: '', side: SIDE.BRIDE }),
              },
              'Cancel'
            ),
            e(
              'button',
              {
                key: 'add',
                type: 'button',
                className: 'wd-btn wd-btn-brand',
                disabled: !composer.name.trim(),
                onClick: () => {
                  // Appended to the day as it stands; drag and the position
                  // select move it from there.
                  addRitual(activeDate, composer.name, composer.side, day.length);
                  setComposer({ open: false, name: '', side: SIDE.BRIDE });
                  setAnnouncement(`${composer.name.trim()} added to the end of this date.`);
                },
              },
              'Add ritual'
            ),
          ]),
        ])
      : e(
          'button',
          {
            key: 'adder',
            type: 'button',
            className: 'wd-adder',
            style: { marginTop: 22 },
            onClick: () => setComposer({ open: true, name: '', side: SIDE.BRIDE }),
          },
          '+ Add a ritual'
        ),
    e(
      'button',
      {
        key: 'next',
        type: 'button',
        className: 'wd-btn wd-btn-brand wd-btn-block',
        style: { marginTop: 12 },
        onClick: () => setPhase('timings'),
      },
      'Event ordering looks right, set the timings'
    ),
    e('div', { className: 'wd-sr-live', key: 'live', 'aria-live': 'polite' }, announcement),
  ]);
};

const TimingPhase = () => {
  const {
    events, activeDate, windows, dayOpens, venues, interviews, sharedLocked,
    closed, readOnly, canEdit, fieldErrors, changeLog, setTiming,
    setCoupleShot,
    setCoupleShotTime, setPhase, setEventConfirmed, setActiveDate,
  } = useWeddingDay();
  // A reader who cannot write sees the day as it stands. The server refuses
  // their writes either way, so an open-looking card would only mislead.
  const viewOnly = readOnly || canEdit === false;
  const shut = sharedLocked || viewOnly;
  // The settled card reopened from its pencil, if any. One at a time, so
  // the walk still has a single open card.
  const [editingId, setEditingId] = useState(null);
  const [announcement, setAnnouncement] = useState('');

  // Settled because the couple closed the card, which is the answer now that
  // nothing on the form is compulsory. A card that already carries both times
  // counts too, so briefs filled before the card was stored still read as
  // answered rather than reopening themselves.
  const isConfirmed = (entry) =>
    isAnswered(entry) && String(entry.id) !== String(editingId);
  const day = activeDate ? eventsOnDate(events, activeDate) : [];

  // The first card still to be settled is the one that is open, unless the
  // couple has gone back into an earlier one. Everything below the open
  // card waits, so the day is timed in the order it runs.
  const firstOpen = day.findIndex((x) => !isConfirmed(x));
  const editAt = editingId
    ? day.findIndex((x) => String(x.id) === String(editingId))
    : -1;
  const openAt = editAt >= 0 ? editAt : (firstOpen < 0 ? day.length : firstOpen);
  const packageWindow = activeDate ? windows[activeDate] : null;
  // A day is often sold with an opening hour and no closing one, so the
  // header reads off the opening and adds the close where there is one.
  // Saying nothing on those days had the hours appear on the last day of
  // the booking alone, which read as a rule that applied only there.
  const packageOpens = activeDate
    ? (packageWindow ? packageWindow.start : dayOpens[activeDate])
    : null;
  // The couple times one date at a time, so the next one is offered here
  // rather than making them go back up to the date pills.
  const dates = datesOf(events);
  const nextDate = dates[dates.indexOf(activeDate) + 1] || null;
  // Either side submitting closes the timeline for both of them, so there is
  // nothing left to offer here once it is in.
  const submittedNote = sharedLocked && !closed && !readOnly;

  // Settling a card hands the day to the next one, so the couple keeps moving
  // without hunting for where they were.
  const confirm = (entry) => {
    setEventConfirmed(entry.id, true);
    const next = day.find((x) => String(x.id) !== String(entry.id) && !isConfirmed(x));
    setAnnouncement(
      next ? `Timing confirmed. Next: ${next.name}.` : 'All timings confirmed for this date.'
    );
    if (!next) return;
    window.requestAnimationFrame(() => {
      const card = document.querySelector(`[data-wd-timing="${next.id}"]`);
      const input = card && card.querySelector('input[type="time"]');
      if (input) input.focus();
    });
  };

  if (!events.length) {
    return e('div', { className: 'wd-empty' }, [
      e('div', { className: 'wd-empty-title', key: 't' }, 'No events yet'),
      e(
        'p',
        { key: 'p' },
        'Your timeline appears here once the events on your booking are set. Contact your coordinator if you expected to see them.'
      ),
    ]);
  }

  return e('div', null, [
    e(DatePills, { key: 'pills' }),
    activeDate
      ? e('div', { className: 'wd-dayhead', key: 'head' }, [
          e('span', { className: 'wd-wd', key: 'wd' }, fmtWeekday(activeDate)),
          e('span', { className: 'wd-dt', key: 'dt' }, fmtDateLong(activeDate)),
          packageOpens
            ? e(
                'span',
                { className: 'wd-win', key: 'win' },
                packageWindow && packageWindow.end
                  ? `PACKAGE HOURS ${fmtTime(packageOpens)} to ${fmtTime(packageWindow.end)}`
                  : `PACKAGE HOURS FROM ${fmtTime(packageOpens)}`
              )
            : null,
        ])
      : null,
    submittedNote
      ? e(
          'div',
          { className: 'wd-approve', key: 'locknote' },
          e(
            'p',
            null,
            'This form has been submitted, so the timeline is closed to both of you now. Ask your coordinator if anything has to move.'
          )
        )
      : null,
    e(
      'div',
      { className: 'wd-tlist', key: 'rows' },
      day.map((entry, i) => {
        // The shot's own hours follow the event's, so it is shown here too:
        // the couple sets the event and reads back when the shot lands. One
        // taken inside the event is drawn on that event's own card, after the
        // times it has to fit inside.
        const inside = entry.couple_shot && entry.couple_shot.position === 'during';
        const shot = entry.couple_shot
          ? e(CoupleShotCard, {
              key: `shot-${entry.id}`,
              entry,
              locked: shut,
              onMove: setCoupleShot,
              onPick: setCoupleShotTime,
              inside,
              timed: true,
            })
          : null;
        const atStart = entry.couple_shot && entry.couple_shot.position === 'start';
        return e('div', { className: 'wd-ogroup', key: String(entry.id) }, [
          // Runs before the couple is needed, so it is drawn above
          // everything this event holds, the shot included. Nothing on it
          // is theirs to set, so it renders itself or renders nothing.
          // Getting ready comes first on the day, so it is drawn above the
          // sitting, which is itself above the shot and the event.
          e(ReadyBlock, { key: 'ready', entry }),
          e(InterviewBlock, { key: 'interview', entry }),
          atStart ? shot : null,
          e(TimingCard, {
            key: 'card',
            entry,
            position: i,
            events,
            windows,
            venues,
            interviews,
            locked: shut,
            stepLocked: i > openAt,
            confirmed: isConfirmed(entry),
            editing: String(entry.id) === String(editingId),
            onConfirm: () => {
              if (String(entry.id) === String(editingId)) {
                // An edit saved. Moving any answer on a settled card
                // unsettles it, so saving has to settle it again or the
                // card reopens itself and asks to be closed a second time.
                // The walk stays where it is: this card was already
                // answered, so there is nowhere to move on to.
                setEventConfirmed(entry.id, true);
                setEditingId(null);
                setAnnouncement(`${entry.name} saved.`);
                return;
              }
              confirm(entry);
            },
            onReopen: () => setEditingId(String(entry.id)),
            error: fieldErrors[`entry:${entry.id}`] || null,
            venueError: fieldErrors[`entry:${entry.id}:venue`] || null,
            linkServerError: fieldErrors[`entry:${entry.id}:map_link`] || null,
            onChange: (patch) => setTiming(entry.id, patch),
          }),
          atStart ? null : shot,
        ]);
      })
    ),
    e(
      'button',
      {
        key: 'back',
        type: 'button',
        className: 'wd-back',
        onClick: () => setPhase('order'),
      },
      '\u2190 Back to event order'
    ),
    nextDate
      ? e(
          'button',
          {
            key: 'nextday',
            type: 'button',
            className: 'wd-back',
            onClick: () => setActiveDate(nextDate),
          },
          `Move to ${fmtDateLong(nextDate)} \u2192`
        )
      : null,
    e(ChangeStrip, { key: 'log', changeLog }),
    e('div', { className: 'wd-sr-live', key: 'live', 'aria-live': 'polite' }, announcement),
  ]);
};

/**
 * The stop after the interview question, asked only where the couple said
 * yes to interviews, and before they order the day. Both sides answer for all
 * four groups: a bride's parents and a groom's parents are two sittings.
 */
/**
 * The interview question, asked here when the creative preferences form has
 * not been answered yet. The same question, the same two options and the
 * same copy afterwards, read off that form's published version, so the
 * couple meets one question rather than two that could disagree.
 */
const InterviewAsk = () => {
  const { interviewQuestion, wantsInterview, sharedLocked, readOnly, canEdit,
    setWantsInterview } = useWeddingDay();
  const shut = sharedLocked || readOnly || canEdit === false;
  const section = interviewQuestion;
  if (!section) return null;
  // The answer as that form stores it, which is what its reveal rules are
  // measured against: the yes copy shows for 'yes', the no copy for 'no'.
  const answer = wantsInterview === true
    ? 'yes'
    : wantsInterview === false ? 'no' : '';
  const group = { answer };

  // Every field of that section, drawn by that form's own field component:
  // the question, the pills, and whichever block the answer reveals. The
  // reveal rules come with the payload, so nothing is decided here.
  return e(
    'div',
    { className: 'wd-ask' },
    (section.fields || []).map((field) =>
      e(Field, {
        key: field.key,
        section: { key: section.key, question: section.question },
        field,
        value: field.key === 'answer' ? answer : undefined,
        group,
        locked: shut,
        onValue: (sectionKey, fieldKey, id) =>
          setWantsInterview(String(id) === 'yes'),
      })
    )
  );
};

const InterviewPhase = () => {
  const { interviews, sharedLocked, readOnly, canEdit, role,
    setInterview, setPhase } = useWeddingDay();
  const shut = sharedLocked || readOnly || canEdit === false;
  // Opens on the side this person is filling in as, because that is the one
  // they came to answer. Both are theirs to set: the sittings are shared.
  const [side, setSide] = useState(role === SIDE.GROOM ? SIDE.GROOM : SIDE.BRIDE);
  // The couple's own sitting is not here any more: it always runs right
  // before they are first needed, so the timeline holds the half hour for
  // it and there is nothing to ask. These tabs hold the two families.
  const sides = [
    { key: SIDE.BRIDE, title: SIDE_LABEL[SIDE.BRIDE] },
    { key: SIDE.GROOM, title: SIDE_LABEL[SIDE.GROOM] },
  ];
  const shown = (key) => interviews.filter(
    (slot) => slot.side === key && slot.group !== COUPLE_GROUP);
  // The day is the answer now: where in it the sitting falls is the
  // coordinator's to place.
  const answered = (key) =>
    shown(key).filter((slot) => slot.date).length;

  return e('div', null, [
    e(
      'p',
      { className: 'wd-intro', key: 'intro' },
      'You asked for interviews with your film. Tell us when each group can sit down, and your coordinator will plan the crew around it.'
    ),
    e(
      'div',
      { className: 'wd-pillrow', key: 'sides', role: 'tablist' },
      sides.map((s) => {
        const total = shown(s.key).length;
        // Marked the same way a filled date is, so the couple can see
        // which tabs are left without opening each one.
        const done = total > 0 && answered(s.key) === total;
        return e(
          'button',
          {
            key: s.key,
            type: 'button',
            role: 'tab',
            'aria-selected': s.key === side,
            'aria-label': `${s.title}, ${answered(s.key)} of ${total} set${done ? ', all filled' : ''}`,
            className: `wd-pill${s.key === side ? ' is-on' : ''}${done ? ' is-done' : ''}`,
            onClick: () => setSide(s.key),
          },
          [
            e('span', { className: 'wd-d', key: 'd' }, s.title),
            e('span', { className: `wd-n${done ? ' is-done' : ''}`, key: 'n' },
              done ? `✓ ${answered(s.key)}/${total}`
                : `${answered(s.key)}/${total}`),
          ]
        );
      })
    ),
    // One card holding the sittings as rows. A sitting asks one thing, so
    // a card each drew three mostly empty boxes down the page.
    e(
      'div',
      { className: 'wd-card wd-interview', key: 'cards' },
      shown(side)
        .map((slot) =>
          e(InterviewCard, {
            key: `${slot.side}:${slot.group}`,
            slot,
            locked: shut,
            onChange: (patch) => setInterview(slot.side, slot.group, patch),
          })
        )
    ),
    // The sittings come before the day is ordered, so this leads on to it.
    e(
      'button',
      {
        key: 'next',
        type: 'button',
        className: 'wd-btn wd-btn-brand wd-btn-block',
        style: { marginTop: 12 },
        onClick: () => setPhase('order'),
      },
      'Sittings done, order your events'
    ),
  ]);
};

const TimelineSection = ({ section, isPhone }) => {
  const { phase, role } = useWeddingDay();

  // Each phase asks for something different, so the copy follows it. The
  // payload can say it better for either one. The interview stops carry
  // their own copy, so the timeline's is not said over them.
  const intro = phase === 'order'
    ? section.question ||
      'Add any smaller rituals and place them in the order they will happen. Timings come next.'
    : phase === 'timings'
    ? section.timing_question ||
      'Now set the start and end time for every event in the order you just confirmed.'
    : null;

  return e('div', null, [
    intro ? e('p', { className: 'wd-intro', key: 'intro' }, intro) : null,
    e(
      'p',
      { className: 'wd-note', key: 'shared' },
      role
        ? `You are filling this in as the ${sideLabel(role).toLowerCase()}. This timeline is shared: your partner sees your changes, and you see theirs.`
        : 'This timeline is shared between both of you. Either of you can change it, and the other sees the change.'
    ),
    phase === 'order'
      ? e(OrderPhase, { key: 'order', isPhone })
      : phase === 'interview_ask'
      ? e(InterviewAsk, { key: 'ask' })
      : phase === 'interviews'
      ? e(InterviewPhase, { key: 'interviews' })
      : e(TimingPhase, { key: 'timings' }),
  ]);
};

export default TimelineSection;
